import React, { useState, useEffect, useRef, useCallback } from 'react';
import adminApi from '../services/adminApi';
import './HeroSectionManager.css';

const SERVER_URL = process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:5000';

const resolveImageUrl = (imagePath) => {
  if (!imagePath) return '';
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://') || imagePath.startsWith('data:')) return imagePath;
  if (imagePath.startsWith('/uploads/')) return `${SERVER_URL}${imagePath}`;
  return imagePath;
};

const HeroSectionManager = () => {
  const [heroSlides, setHeroSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedSlide, setSelectedSlide] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const fetchSlides = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminApi.getHeroSlides();
      if (data.success && data.slides) {
        setHeroSlides(data.slides.map(slide => ({
          ...slide, id: slide._id,
          image: resolveImageUrl(slide.image),
          imageMobile: resolveImageUrl(slide.imageMobile),
        })));
      }
    } catch (error) {
      setErrorMsg('Failed to load slides');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSlides(); }, [fetchSlides]);

  const activeSlides = heroSlides.filter(s => s.isActive);
  const sortedSlides = [...heroSlides].sort((a, b) => a.order - b.order);

  const showSuccess = (msg) => { setSuccessMsg(msg); setErrorMsg(''); setTimeout(() => setSuccessMsg(''), 3000); };
  const showError = (msg) => { setErrorMsg(msg); setSuccessMsg(''); setTimeout(() => setErrorMsg(''), 5000); };

  const moveSlide = async (index, direction) => {
    const newSlides = [...sortedSlides];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newSlides.length) return;
    [newSlides[index], newSlides[targetIndex]] = [newSlides[targetIndex], newSlides[index]];
    try { await adminApi.reorderHeroSlides(newSlides.map(s => s._id)); await fetchSlides(); showSuccess('Slide reordered'); }
    catch { showError('Failed to reorder'); }
  };

  const handleAdd = async (formData) => {
    try { await adminApi.createHeroSlide(formData); await fetchSlides(); setShowAddModal(false); showSuccess('Slide added'); }
    catch (error) { showError(error.message || 'Failed to add'); }
  };

  const handleUpdate = async (formData) => {
    try { await adminApi.updateHeroSlide(selectedSlide._id, formData); await fetchSlides(); setShowEditModal(false); setSelectedSlide(null); showSuccess('Slide updated'); }
    catch (error) { showError(error.message || 'Failed to update'); }
  };

  const handleDelete = async () => {
    if (!selectedSlide) return;
    try { await adminApi.deleteHeroSlide(selectedSlide._id); await fetchSlides(); setShowDeleteModal(false); setSelectedSlide(null); showSuccess('Slide deleted'); }
    catch { showError('Failed to delete'); }
  };

  const handleToggle = async (id) => {
    try { await adminApi.toggleHeroSlide(id); await fetchSlides(); showSuccess('Status updated'); }
    catch { showError('Failed to toggle'); }
  };

  const handleReset = async () => {
    if (!window.confirm('Delete ALL slides? This cannot be undone.')) return;
    try { await adminApi.resetHeroSlides(); await fetchSlides(); showSuccess('All slides deleted'); }
    catch { showError('Failed to reset'); }
  };

  if (loading) {
    return (
      <div className="hm">
        <div className="hm__page-header"><div><h1 className="hm__page-title">Hero Section Manager</h1><p className="hm__page-desc">Loading slides...</p></div></div>
        <div className="hm__empty"><div className="hm__loader" /><p>Loading slides from server...</p></div>
      </div>
    );
  }

  return (
    <div className="hm">
      {/* Header */}
      <div className="hm__page-header">
        <div>
          <h1 className="hm__page-title">Hero Section Manager</h1>
          <p className="hm__page-desc">{activeSlides.length} active of {heroSlides.length} total slides</p>
        </div>
        <div className="hm__header-actions">
          <button className="hm__btn hm__btn--ghost" onClick={handleReset}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
            Delete All
          </button>
          <button className="hm__btn hm__btn--primary" onClick={() => setShowAddModal(true)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Slide
          </button>
        </div>
      </div>

      {/* Messages */}
      {successMsg && (
        <div className="hm__msg hm__msg--success">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          {successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="hm__msg hm__msg--error">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
          {errorMsg}
        </div>
      )}

      {/* Slides */}
      {sortedSlides.length === 0 ? (
        <div className="hm__empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
          <h3>No slides yet</h3>
          <p>Add your first hero carousel slide</p>
          <button className="hm__btn hm__btn--primary" onClick={() => setShowAddModal(true)}>Add First Slide</button>
        </div>
      ) : (
        <div className="hm__grid">
          {sortedSlides.map((slide, index) => (
            <div key={slide._id} className={`hm__card ${!slide.isActive ? 'hm__card--inactive' : ''}`}>
              <div className="hm__card-img">
                <img src={slide.image} alt={slide.title} />
                {!slide.isActive && <div className="hm__card-badge hm__card-badge--inactive">Inactive</div>}
                <div className="hm__card-badge hm__card-badge--order">#{index + 1}</div>
                <div className="hm__card-overlay">
                  <button className="hm__card-action" onClick={() => { setSelectedSlide(slide); setShowPreviewModal(true); }} title="Preview">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  </button>
                  <button className="hm__card-action" onClick={() => { setSelectedSlide(slide); setShowEditModal(true); }} title="Edit">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  </button>
                  <button className="hm__card-action hm__card-action--danger" onClick={() => { setSelectedSlide(slide); setShowDeleteModal(true); }} title="Delete">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                  </button>
                </div>
              </div>
              <div className="hm__card-body">
                <h3 className="hm__card-title">{slide.title}</h3>
                <p className="hm__card-subtitle">{slide.subtitle}</p>
                <div className="hm__card-footer">
                  <label className="hm__toggle">
                    <input type="checkbox" checked={slide.isActive} onChange={() => handleToggle(slide._id)} />
                    <span className="hm__toggle-track" />
                    <span className="hm__toggle-label">{slide.isActive ? 'Active' : 'Inactive'}</span>
                  </label>
                  <div className="hm__reorder">
                    <button className="hm__reorder-btn" onClick={() => moveSlide(index, 'up')} disabled={index === 0}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="18 15 12 9 6 15"/></svg>
                    </button>
                    <button className="hm__reorder-btn" onClick={() => moveSlide(index, 'down')} disabled={index === sortedSlides.length - 1}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && <SlideFormModal title="Add New Slide" onClose={() => setShowAddModal(false)} onSave={handleAdd} />}

      {/* Edit Modal */}
      {showEditModal && selectedSlide && <SlideFormModal title="Edit Slide" initialData={selectedSlide} onClose={() => { setShowEditModal(false); setSelectedSlide(null); }} onSave={handleUpdate} />}

      {/* Delete Modal */}
      {showDeleteModal && selectedSlide && (
        <div className="hm__modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="hm__modal hm__modal--sm" onClick={(e) => e.stopPropagation()}>
            <div className="hm__delete-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
            </div>
            <h3 className="hm__delete-title">Delete Slide?</h3>
            <p className="hm__delete-text">Are you sure you want to delete "<strong>{selectedSlide.title}</strong>"? This cannot be undone.</p>
            <div className="hm__delete-actions">
              <button className="hm__btn hm__btn--ghost" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button className="hm__btn hm__btn--danger" onClick={handleDelete}>Delete Slide</button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedSlide && (
        <div className="hm__modal-overlay" onClick={() => { setShowPreviewModal(false); setSelectedSlide(null); }}>
          <div className="hm__modal hm__modal--preview" onClick={(e) => e.stopPropagation()}>
            <button className="hm__modal-close" onClick={() => { setShowPreviewModal(false); setSelectedSlide(null); }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
            <div className="hm__preview-img">
              <img src={selectedSlide.image} alt={selectedSlide.title} />
              <div className="hm__preview-text">
                <h2>{selectedSlide.title}</h2>
                <p>{selectedSlide.subtitle}</p>
              </div>
            </div>
            {selectedSlide.imageMobile && (
              <div className="hm__preview-mobile">
                <p className="hm__preview-label">Mobile Version:</p>
                <img src={selectedSlide.imageMobile} alt={`${selectedSlide.title} - Mobile`} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══ SLIDE FORM MODAL ═══ */
const SlideFormModal = ({ title, initialData, onClose, onSave }) => {
  const [slideTitle, setSlideTitle] = useState(initialData?.title || '');
  const [subtitle, setSubtitle] = useState(initialData?.subtitle || '');
  const [imageFile, setImageFile] = useState(null);
  const [imageMobileFile, setImageMobileFile] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [imageMobileUrl, setImageMobileUrl] = useState('');
  const [imagePreview, setImagePreview] = useState(initialData?.image || '');
  const [imageMobilePreview, setImageMobilePreview] = useState(initialData?.imageMobile || '');
  const [isActive, setIsActive] = useState(initialData?.isActive !== undefined ? initialData.isActive : true);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const imageInputRef = useRef(null);
  const imageMobileInputRef = useRef(null);

  useEffect(() => {
    if (initialData?.image) { const img = initialData.image; if (img.startsWith('http') && !img.includes('/uploads/')) setImageUrl(img); }
    if (initialData?.imageMobile) { const img = initialData.imageMobile; if (img.startsWith('http') && !img.includes('/uploads/')) setImageMobileUrl(img); }
  }, [initialData]);

  const handleImageUpload = (e, type) => {
    const file = e.target.files[0]; if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!validTypes.includes(file.type)) { setErrors(p => ({ ...p, [type]: 'Upload JPG, PNG, or WebP' })); return; }
    if (file.size > 5 * 1024 * 1024) { setErrors(p => ({ ...p, [type]: 'Max 5MB' })); return; }
    setErrors(p => ({ ...p, [type]: '' }));
    const reader = new FileReader();
    reader.onload = (ev) => {
      if (type === 'desktop') { setImageFile(file); setImageUrl(''); setImagePreview(ev.target.result); }
      else { setImageMobileFile(file); setImageMobileUrl(''); setImageMobilePreview(ev.target.result); }
    };
    reader.readAsDataURL(file);
  };

  const removeImage = (type) => {
    if (type === 'desktop') { setImageFile(null); setImageUrl(''); setImagePreview(''); if (imageInputRef.current) imageInputRef.current.value = ''; }
    else { setImageMobileFile(null); setImageMobileUrl(''); setImageMobilePreview(''); if (imageMobileInputRef.current) imageMobileInputRef.current.value = ''; }
  };

  const handleUrlInput = (url, type) => {
    if (type === 'desktop') { setImageUrl(url); setImageFile(null); setImagePreview(url); if (imageInputRef.current) imageInputRef.current.value = ''; }
    else { setImageMobileUrl(url); setImageMobileFile(null); setImageMobilePreview(url); if (imageMobileInputRef.current) imageMobileInputRef.current.value = ''; }
  };

  const handleSave = async () => {
    const errs = {};
    if (!slideTitle.trim()) errs.title = 'Title is required';
    if (!subtitle.trim()) errs.subtitle = 'Subtitle is required';
    if (!initialData && !imageFile && !imageUrl) errs.desktop = 'Desktop image is required';
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('title', slideTitle.trim());
      formData.append('subtitle', subtitle.trim());
      formData.append('isActive', isActive);
      if (imageFile) formData.append('image', imageFile); else if (imageUrl) formData.append('imageUrl', imageUrl);
      if (imageMobileFile) formData.append('imageMobile', imageMobileFile); else if (imageMobileUrl) formData.append('imageMobileUrl', imageMobileUrl);
      await onSave(formData);
    } catch (error) { setErrors({ general: error.message || 'Failed to save' }); }
    finally { setSaving(false); }
  };

  return (
    <div className="hm__modal-overlay" onClick={onClose}>
      <div className="hm__modal hm__modal--form" onClick={(e) => e.stopPropagation()}>
        <div className="hm__modal-header">
          <h2>{title}</h2>
          <button className="hm__modal-close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div className="hm__modal-body">
          {errors.general && <div className="hm__msg hm__msg--error" style={{ margin: 0 }}>{errors.general}</div>}
          <div className="hm__form-field">
            <label className="hm__form-label">Title <span className="hm__required">*</span></label>
            <input type="text" value={slideTitle} onChange={(e) => { setSlideTitle(e.target.value); setErrors(p => ({ ...p, title: '' })); }}
              placeholder="e.g. Discover Gurudongmar Lake" className={`hm__form-input ${errors.title ? 'hm__form-input--error' : ''}`} />
            {errors.title && <span className="hm__form-error">{errors.title}</span>}
          </div>
          <div className="hm__form-field">
            <label className="hm__form-label">Subtitle <span className="hm__required">*</span></label>
            <textarea value={subtitle} onChange={(e) => { setSubtitle(e.target.value); setErrors(p => ({ ...p, subtitle: '' })); }}
              placeholder="Brief description" className={`hm__form-textarea ${errors.subtitle ? 'hm__form-input--error' : ''}`} rows={3} />
            {errors.subtitle && <span className="hm__form-error">{errors.subtitle}</span>}
          </div>
          <div className="hm__form-field">
            <label className="hm__form-label">Desktop Image <span className="hm__required">*</span> <span className="hm__form-hint">1920×1080px</span></label>
            {imagePreview ? (
              <div className="hm__img-preview">
                <img src={imagePreview} alt="Desktop preview" />
                <button className="hm__img-remove" onClick={() => removeImage('desktop')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg> Remove
                </button>
              </div>
            ) : (
              <div className="hm__upload-area">
                <input type="file" ref={imageInputRef} accept="image/jpeg,image/png,image/webp" onChange={(e) => handleImageUpload(e, 'desktop')} className="hm__file-input" id="desktop-img" />
                <label htmlFor="desktop-img" className="hm__upload-label">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  <span>Click to upload</span><span className="hm__upload-hint">JPG, PNG, WebP (max 5MB)</span>
                </label>
              </div>
            )}
            <div className="hm__url-row">
              <span className="hm__url-or">or paste URL:</span>
              <input type="text" value={imageUrl} onChange={(e) => { handleUrlInput(e.target.value, 'desktop'); setErrors(p => ({ ...p, desktop: '' })); }}
                placeholder="https://example.com/image.jpg" className="hm__form-input hm__form-input--sm" />
            </div>
            {errors.desktop && <span className="hm__form-error">{errors.desktop}</span>}
          </div>
          <div className="hm__form-field">
            <label className="hm__form-label">Mobile Image <span className="hm__form-hint">Optional • 800×1200px</span></label>
            {imageMobilePreview ? (
              <div className="hm__img-preview hm__img-preview--mobile">
                <img src={imageMobilePreview} alt="Mobile preview" />
                <button className="hm__img-remove" onClick={() => removeImage('mobile')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg> Remove
                </button>
              </div>
            ) : (
              <div className="hm__upload-area hm__upload-area--sm">
                <input type="file" ref={imageMobileInputRef} accept="image/jpeg,image/png,image/webp" onChange={(e) => handleImageUpload(e, 'mobile')} className="hm__file-input" id="mobile-img" />
                <label htmlFor="mobile-img" className="hm__upload-label">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  <span>Upload mobile image</span>
                </label>
              </div>
            )}
            <div className="hm__url-row">
              <span className="hm__url-or">or paste URL:</span>
              <input type="text" value={imageMobileUrl} onChange={(e) => handleUrlInput(e.target.value, 'mobile')}
                placeholder="https://example.com/mobile.jpg" className="hm__form-input hm__form-input--sm" />
            </div>
          </div>
          <div className="hm__form-field">
            <label className="hm__toggle">
              <input type="checkbox" checked={isActive} onChange={() => setIsActive(!isActive)} />
              <span className="hm__toggle-track" />
              <span className="hm__toggle-label">{isActive ? 'Active (visible)' : 'Inactive (hidden)'}</span>
            </label>
          </div>
        </div>
        <div className="hm__modal-footer">
          <button className="hm__btn hm__btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="hm__btn hm__btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : initialData ? 'Save Changes' : 'Add Slide'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HeroSectionManager;
